import React, { useEffect, useState } from 'react';
import { Form, Select, Button, message, Spin, Row, Col, Table, Avatar } from 'antd';
import axiosInstance from '../utils/axiosInstance';
import { useNavigate } from 'react-router-dom';
import { config } from '../utils';
import { DeleteOutlined, UserOutlined,FileTextOutlined } from '@ant-design/icons';

const { Option } = Select;

interface Officer {
    id: string;
    officer_name: string;
    email: string;
    phone_number: string;
    img?: string;
}

interface Constituency {
    id: string;
    constituency: string;
    region: string;
}

const AssignOfficerToConstituency = () => {
    const [form] = Form.useForm();
    const [officers, setOfficers] = useState<Officer[]>([]);
    const [constituencies, setConstituencies] = useState<Constituency[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [selectedOfficer, setSelectedOfficer] = useState<Officer | null>(null);
    const [assignedConstituencies, setAssignedConstituencies] = useState<Constituency[]>([]);
    const navigate = useNavigate();

    useEffect(() => {
        fetchOfficers();
        fetchConstituencies();
    }, []);

    const fetchOfficers = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(config.api.endpoints.officers);
            setOfficers(response.data);
        } catch (error) {
            message.error('Failed to fetch officers');
        } finally {
            setLoading(false);
        }
    };

    const fetchConstituencies = async () => {
        setLoading(true);

        // Check if constituencies are stored in session storage
        const storedConstituencies = sessionStorage.getItem('constituencies');
        if (storedConstituencies) {
            setConstituencies(JSON.parse(storedConstituencies));
            setLoading(false);
            return;
        }

        try {
            const response = await axiosInstance.get(config.api.endpoints.constituencies);
            setConstituencies(response.data);

            // Store constituencies in session storage
            sessionStorage.setItem('constituencies', JSON.stringify(response.data));
        } catch (error) {
            message.error('Failed to fetch constituencies');
        } finally {
            setLoading(false);
        }
    };

    const fetchAssignedConstituencies = async (officerId: string) => {
        try {
            const response = await axiosInstance.get(
                `${config.api.endpoints.assigned_constituencies}/${officerId}`
            );
            const officerData = response.data;
            setSelectedOfficer({
                id: officerData.id,
                officer_name: officerData.officer_name,
                email: officerData.email,
                phone_number: officerData.phone_number,
                img: officerData.img,
            });
            setAssignedConstituencies(officerData.assigned_constituencies);

            // Extract constituency IDs and set them in the form to pre-select
            const assignedConstituencyIds = officerData.assigned_constituencies.map(
                (c: Constituency) => c.id
            );
            form.setFieldsValue({ constituency_ids: assignedConstituencyIds });
        } catch (error) {
            message.error('Failed to fetch assigned constituencies');
        }
    };

    const onFinish = async (values: any) => {
        setSubmitting(true);
        try {
            const payload = {
                officer_id: values.officer_id,
                constituency_ids: values.constituency_ids,
            };

            await axiosInstance.post(config.api.endpoints.assign_constituencies, payload);
            message.success('Constituencies assigned successfully');
            // Fetch updated assigned constituencies
            fetchAssignedConstituencies(values.officer_id);
        } catch (error: any) {
            if (error.response && error.response.data.detail) {
                message.error(error.response.data.detail);
            } else {
                message.error('Failed to assign constituencies');
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleOfficerChange = (value: string) => {
        // When the officer is changed, fetch their assigned constituencies
        fetchAssignedConstituencies(value);
        // No need to reset 'constituency_ids' as we set it in 'fetchAssignedConstituencies'
    };

    // Unassign Functionality
    const unassignConstituency = async (constituencyId: string) => {
        if (!selectedOfficer) return;
        try {
            await axiosInstance.delete(
                `${config.api.endpoints.assigned_constituencies}/${selectedOfficer.id}/${constituencyId}`
            );
            message.success('Constituency unassigned successfully');
            // Update the assigned constituencies list
            fetchAssignedConstituencies(selectedOfficer.id);
        } catch (error) {
            message.error('Failed to unassign constituency');
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-full">
                <Spin tip="Loading data..." />
            </div>
        );
    }

    const columns = [
        {
            title: 'Constituency',
            dataIndex: 'constituency',
            key: 'constituency',
        },
        {
            title: 'Region',
            dataIndex: 'region',
            key: 'region',
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (text: any, record: Constituency) => (
                <Button
                    type="link"
                    icon={<DeleteOutlined />}
                    danger
                    onClick={() => unassignConstituency(record.id)}
                />
            ),
        },
    ];

    return (
        <div className="p-8">
           
            <div className="flex justify-between items-center mb-6">
            <div className="flex items-center p-5 gap-2">
            <FileTextOutlined className="text-2xl text-red-600" />
               <p className="text-md text-black font-bold">  Assign Officers</p>
            </div>  
         
            <Button onClick={() => navigate('/settings')} >
                Back 
            </Button> 
            </div>          
             <Row gutter={16}>
                {/* Left Side: Assignment Form */}
                <Col xs={24} md={12}>
                    <Form form={form} layout="vertical" onFinish={onFinish}>
                        {/* Officer Selection */}
                        <Form.Item
                            label="Select Officer"
                            name="officer_id"
                            rules={[{ required: true, message: 'Please select an officer!' }]}
                        >
                            <Select
                                placeholder="Select an officer"
                                showSearch
                                optionFilterProp="children"
                                onChange={handleOfficerChange}
                            >
                                {officers.map((officer) => (
                                    <Option key={officer.id} value={officer.id}>
                                        {officer.officer_name}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>

                        {/* Constituency Selection */}
                        <Form.Item
                            label="Select Constituencies"
                            name="constituency_ids"
                            rules={[
                                { required: true, message: 'Please select at least one constituency!' },
                            ]}
                        >
                            <Select
                                mode="multiple"
                                placeholder="Select constituencies"
                                showSearch
                                optionFilterProp="children"
                            >
                                {constituencies.map((constituency) => (
                                    <Option key={constituency.id} value={constituency.id}>
                                        {constituency.constituency} - {constituency.region}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>

                        {/* Submit Button */}
                        <Form.Item>
                            <Button type="primary" htmlType="submit" loading={submitting}>
                                Assign Constituencies
                            </Button>
                        </Form.Item>
                    </Form>
                </Col>

                {/* Right Side: Officer Details and Assigned Constituencies */}
                <Col xs={24} md={12}>
                    {selectedOfficer ? (
                        <div>
                            <h2 className="text-xl font-bold mb-4 text-red-700">Officer Details</h2>
                            <div className="flex items-center mb-4">
                                <Avatar
                                    src={selectedOfficer.img}
                                    size={64}
                                    icon={<UserOutlined />}
                                />
                                <div className="ml-4">
                                    <p>
                                        <strong>Name:</strong> {selectedOfficer.officer_name}
                                    </p>
                                    <p>
                                        <strong>Email:</strong> {selectedOfficer.email}
                                    </p>
                                    <p>
                                        <strong>Phone:</strong> {selectedOfficer.phone_number}
                                    </p>
                                </div>
                            </div>
                            <h3 className="text-lg font-bold mb-2">Assigned Constituencies</h3>
                            <Table
                                dataSource={assignedConstituencies}
                                columns={columns}
                                rowKey="id"
                                pagination={{ pageSize: 5 }}
                            />
                        </div>
                    ) : (
                        <div>
                            <h2 className="text-xl font-bold mb-4">Officer Details</h2>
                            <p>Please select an officer to view details and assigned constituencies.</p>
                        </div>
                    )}
                </Col>
            </Row>
        </div>
    );
};

export default AssignOfficerToConstituency;